import React, { useState } from 'react';
import { Calendar, Plus, X } from 'lucide-react';
import CreateEvent from '../components/CreateEvent'; // Assuming CreateEvent is in the components folder
import EventRealDataTable from '../components/tables/EventRealDataTable';
import { UserRole } from '@/types';

interface Event {
  id: string;
  name: string;
  description: string | null;
  date: string;
  location: string;
  image: string | null;
  organizer: { name: string; };
  venue: { venueName: string; };
  totalTickets: number;
  ticketPrice: number;
  ticketCurrency: string;
}

interface EventsPageProps {
  currentUserId: string;
  currentUserRole: UserRole;
}

const EventsPage: React.FC<EventsPageProps> = ({ currentUserId, currentUserRole }) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [viewingEvent, setViewingEvent] = useState<Event | null>(null);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Calendar className="text-kala-secondary" /> Events
          </h2>
          <p className="text-kala-400 text-sm">Browse and manage upcoming events.</p>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="bg-kala-secondary text-kala-900 font-bold px-4 py-2 rounded-lg hover:bg-cyan-400 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Create Event
        </button>
      </div>

      <EventRealDataTable currentUserRole={currentUserRole} currentUserId={currentUserId} />

      {/* Create Event Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in" onClick={() => setIsCreateModalOpen(false)}>
          <div className="bg-kala-900 border border-kala-700 rounded-2xl w-full max-w-4xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
             <div className="p-4 bg-kala-800 border-b border-kala-700 flex justify-between items-center">
                 <h3 className="text-white font-bold">Create New Event</h3>
                 <button onClick={() => setIsCreateModalOpen(false)} className="text-kala-400 hover:text-white"><X className="w-5 h-5" /></button>
             </div>
             <div className="p-6">
                <CreateEvent />
             </div>
          </div>
        </div>
      )}

      {/* View Event Modal (Kept for potential future use) */}
      {viewingEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in" onClick={() => setViewingEvent(null)}>
          <div className="bg-kala-900 border border-kala-700 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="relative">
                <img src={viewingEvent.image || 'https://picsum.photos/seed/event/800/400'} alt={viewingEvent.name} className="w-full h-64 object-cover" />
                <button onClick={() => setViewingEvent(null)} className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2"><X className="w-6 h-6" /></button>
            </div>
            <div className="p-6">
                <h2 className="text-3xl font-bold text-white mb-2">{viewingEvent.name}</h2>
                <p className="text-lg text-kala-300 mb-4">{new Date(viewingEvent.date).toLocaleString()}</p>
                <p className="text-kala-400 mb-4">{viewingEvent.description}</p>
                <div className="text-sm">
                    <p><span className="font-bold">Location:</span> {viewingEvent.location}</p>
                    <p><span className="font-bold">Venue:</span> {viewingEvent.venue.venueName}</p>
                    <p><span className="font-bold">Organizer:</span> {viewingEvent.organizer.name}</p>
                    <p><span className="font-bold">Tickets:</span> {viewingEvent.totalTickets} @ {viewingEvent.ticketPrice} {viewingEvent.ticketCurrency}</p>
                </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsPage;
